import { useState, useEffect } from "react";
import { getSalesDashboard } from "../../services/salesDashboard";
import SummaryCards from "../../components/dashboard/SummaryCards";
import OrderTypeCards from "../../components/dashboard/OrderTypeCards";
import ThirdPartyBreakdown from "../../components/dashboard/ThirdPartyBreakdown";
import IndiaMap from "../../components/dashboard/IndiaMap";
import CategoryChart from "../../components/dashboard/CategoryChart";
import SalespersonTable from "../../components/dashboard/SalespersonTable";
import SkeletonDashboard from "../../components/dashboard/SkeletonDashboard";
import ErrorBanner from "../../components/dashboard/ErrorBanner";
import DateRangePicker from "../../components/inputFields/DateRangePicker";
import { Download } from "lucide-react";
import SalesSheetExportModal from "../../components/dashboard/SalesSheetExportModal";

function getFirstDayOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function getToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateLabel(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${parseInt(d)} ${months[parseInt(m) - 1]} ${y}`;
}

const SalesDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState(getToday());
  const [subtitle, setSubtitle] = useState(
    `${formatDateLabel(getFirstDayOfMonth())} – ${formatDateLabel(getToday())}`
  );
  const [selectedState, setSelectedState] = useState(null);

  const [showSalesSheetModal, setShowSalesSheetModal] = useState(false);

  const fetchDashboard = async (start, end, state) => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      if (state) params.state = state;
      const res = await getSalesDashboard(params);
      setData(res.data);
      setSubtitle(
        `${formatDateLabel(res.data?.meta?.start_date)} – ${formatDateLabel(res.data?.meta?.end_date)}`
      );
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard(startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRangeChange = ({ start, end }) => {
    if (start && end) {
      if (start > end) return;
      setStartDate(start);
      setEndDate(end);
      fetchDashboard(start, end, selectedState);
      return;
    }
    if (!start && !end) {
      const s = getFirstDayOfMonth();
      const e = getToday();
      setStartDate(s);
      setEndDate(e);
      fetchDashboard(s, e, selectedState);
    }
  };

  const handleStateClick = (stateName) => {
    // Clicking the selected state again clears the filter
    const next = stateName === selectedState ? null : stateName;
    setSelectedState(next);
    fetchDashboard(startDate, endDate, next);
  };

  return (
    <div className="p-3 md:p-5 space-y-4 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-visible">
        <div className="flex flex-col gap-4 px-4 py-4 sm:px-5 sm:py-4 lg:flex-row lg:items-center lg:justify-between lg:gap-6 bg-gradient-to-br from-white via-white to-slate-50/60">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-gray-900 tracking-tight">Sales Stats</h1>
              {loading && (
                <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-blue-500" title="Loading" />
              )}
            </div>
            <p className="mt-1 flex items-center gap-2 text-xs text-gray-500">
              <span className="inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500 ring-4 ring-emerald-500/15" />
              <span className="truncate">{subtitle}</span>
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center lg:w-auto lg:min-w-[300px]">
            <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              Report period
            </span>
            <DateRangePicker
              compact
              className="w-full sm:flex-1 lg:max-w-md"
              value={{ start: startDate, end: endDate }}
              onChange={handleRangeChange}
              placeholder="Choose start & end date"
            />
            <button
              type="button"
              title="Download Sales Sheet"
              aria-label="Download Sales Sheet"
              onClick={() => setShowSalesSheetModal(true)}
              className="bg-primary text-white rounded-xl cursor-pointer p-2.5 hover:opacity-90 transition-all shrink-0 flex items-center justify-center shadow-md shadow-primary/10 active:scale-95"
            >
              <Download size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && <ErrorBanner onRetry={() => fetchDashboard(startDate, endDate, selectedState)} />}

      {/* Loading */}
      {loading && <SkeletonDashboard />}

      {/* Content */}
      {!loading && data && (
        <>
          <SummaryCards summary={data.summary} />
          <OrderTypeCards
            returnsCancellations={data.returns_cancellations}
            orderType={data.order_type}
          />
          {data.third_party_breakdown?.length > 0 && (
            <ThirdPartyBreakdown breakdown={data.third_party_breakdown} />
          )}
          <div className="grid grid-cols-1 gap-4">
            <IndiaMap
              districtWise={data.district_wise}
              districtUnmapped={data.district_unmapped}
              selectedState={selectedState}
              onStateClick={handleStateClick}
              productsByState={data.products_by_state}
              startDate={startDate}
              endDate={endDate}
            />
          </div>
          <div className="grid grid-cols-1 gap-4">
            <CategoryChart categoryWise={data.category_wise} />
          </div>
          {data.salesperson_wise?.length > 0 && (
            <SalespersonTable salespersonWise={data.salesperson_wise} />
          )}
        </>
      )}
      {showSalesSheetModal && (
        <SalesSheetExportModal startDate={startDate} endDate={endDate} onClose={() => setShowSalesSheetModal(false)} />
      )}
    </div>
  );
};

export default SalesDashboard;
